import {
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  User,
  AuthError,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { auth, db, analytics } from './firebase';
import { UserDocument, ClimateZone, Hemisphere } from '../types/user';

const googleProvider = new GoogleAuthProvider();

export type AuthErrorCode =
  | 'auth/popup-closed-by-user'
  | 'auth/cancelled-popup-request'
  | 'auth/network-request-failed'
  | 'auth/popup-blocked'
  | 'auth/account-disabled'
  | 'unknown';

export interface AuthResult {
  success: boolean;
  error?: string;
  errorCode?: AuthErrorCode;
  silent?: boolean;
}

const ERROR_MESSAGES: Record<string, { message: string; silent?: boolean }> = {
  'auth/popup-closed-by-user': { message: '', silent: true },
  'auth/cancelled-popup-request': { message: '', silent: true },
  'auth/network-request-failed': { message: "Couldn't sign in. Please try again." },
  'auth/popup-blocked': { message: 'Please allow popups for this site to sign in.' },
  'auth/account-disabled': { message: 'This account has been disabled.' },
};

function getErrorMessage(error: AuthError): { message: string; silent: boolean; code: AuthErrorCode } {
  const errorInfo = ERROR_MESSAGES[error.code];
  if (errorInfo) {
    return {
      message: errorInfo.message,
      silent: errorInfo.silent || false,
      code: error.code as AuthErrorCode,
    };
  }
  return {
    message: "Couldn't sign in. Please try again.",
    silent: false,
    code: 'unknown',
  };
}

export async function signInWithGoogle(): Promise<AuthResult> {
  try {
    await signInWithPopup(auth, googleProvider);
    if (analytics) {
      logEvent(analytics, 'login', { method: 'google' });
    }
    return { success: true };
  } catch (error) {
    const authError = error as AuthError;
    const { message, silent, code } = getErrorMessage(authError);
    console.error('Failed to sign in with Google:', authError.code, authError.message);
    if (analytics && !silent) {
      logEvent(analytics, 'login_failure', { error_code: authError.code });
    }
    return { success: false, error: message, errorCode: code, silent };
  }
}

export async function signOutUser(): Promise<void> {
  try {
    await firebaseSignOut(auth);
    if (analytics) {
      logEvent(analytics, 'logout');
    }
  } catch (error) {
    console.error('Failed to sign out:', error);
  }
}

export async function createUserDocument(user: User): Promise<void> {
  const userRef = doc(db, 'users', user.uid);

  const maxRetries = 3;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await setDoc(userRef, {
          email: user.email || '',
          displayName: user.displayName || 'Plant Lover',
          photoURL: user.photoURL || '',
          tier: 'free',
          bonusIdentifications: 25,
          bonusUsed: false,
          city: null,
          climateZone: null,
          hemisphere: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      return;
    } catch (error) {
      lastError = error;
      console.error(`Failed to create user document (attempt ${attempt + 1}/${maxRetries}):`, error);

      if (attempt < maxRetries - 1) {
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

export async function updateUserCity(
  uid: string,
  city: string,
  climateZone: ClimateZone,
  hemisphere: Hemisphere
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      city,
      climateZone,
      hemisphere,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to update user city:', error);
    return { success: false, error: 'Could not save city. Please try again.' };
  }
}

export async function getUserDocument(uid: string): Promise<UserDocument | null> {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data() as UserDocument;
    }
    return null;
  } catch (error) {
    console.error('Failed to get user document:', error);
    throw error;
  }
}
